import React, { useEffect, useRef, useState } from 'react';
import './CampusSafety.css';

const PhoneIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.64 3.35 2 2 0 0 1 3.62 1h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 8.6a16 16 0 0 0 6 6l.96-.96a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
  </svg>
);

const ChevronRightIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"></polyline>
  </svg>
);

const LocationIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
    <circle cx="12" cy="10" r="3"></circle>
  </svg>
);

const DISMISS_THRESHOLD = 100;

const CampusSafety = ({ isOpen, onClose }) => {
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startYRef = useRef(0);
  const isDraggingRef = useRef(false);
  const sheetRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setDragY(0);
      setIsDragging(false);
      isDraggingRef.current = false;
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const sheet = sheetRef.current;
    if (!sheet) return;
    const onTouchMove = (e) => {
      if (isDraggingRef.current) e.preventDefault();
    };
    sheet.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => sheet.removeEventListener('touchmove', onTouchMove);
  }, [isOpen]);

  const handleTouchStart = (e) => {
    startYRef.current = e.touches[0].clientY;
    setIsDragging(true);
    isDraggingRef.current = true;
  };

  const handleTouchMove = (e) => {
    const delta = e.touches[0].clientY - startYRef.current;
    if (delta > 0) {
      setDragY(delta);
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    isDraggingRef.current = false;
    if (dragY > DISMISS_THRESHOLD) {
      setDragY(window.innerHeight);
      setTimeout(onClose, 220);
    } else {
      setDragY(0);
    }
  };

  if (!isOpen) return null;

  const overlayOpacity = Math.max(0, 1 - dragY / 300);

  return (
    <>
      <div
        className="campus-safety-overlay"
        onClick={onClose}
        style={{ opacity: overlayOpacity }}
      />
      <div
        ref={sheetRef}
        className="campus-safety-sheet"
        role="dialog"
        aria-label="Campus Safety"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          transform: `translateY(${dragY}px)`,
          transition: isDragging ? 'none' : 'transform 0.25s ease',
        }}
      >
        <div className="campus-safety-drag-handle" />
        <div className="campus-safety-header">
          <h2>Campus Safety</h2>
          <button className="campus-safety-close" onClick={onClose} aria-label="Close">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="campus-safety-body">
          <a href="tel:6087963911" className="campus-safety-call-btn">
            <div className="call-icon">
              <PhoneIcon />
            </div>
            <div className="call-info">
              <span className="call-label">Campus Security</span>
              <span className="call-number">(608) 796-3911</span>
            </div>
            <ChevronRightIcon />
          </a>
          <div className="campus-safety-location">
            <LocationIcon />
            <span>Marian Hall 117</span>
          </div>

          <div className="campus-safety-divider" />

          <h3 className="campus-safety-section-title">Resources</h3>

          <a
            href="https://www.viterbo.edu/mission-and-ministry/viterbo-cares"
            className="campus-safety-resource-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            <div className="resource-info">
              <strong>Viterbo Cares</strong>
              <p>Connect with resources for food, housing, health care, finances, mental health, transportation, and other everyday needs</p>
            </div>
            <ChevronRightIcon />
          </a>

          <a
            href="https://www.viterbo.edu/student-life-division/viterbo-speaks"
            className="campus-safety-resource-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            <div className="resource-info">
              <strong>Viterbo Speaks Up</strong>
              <p>Report crimes, harassment, sexual violence, discrimination, code of conduct violations, and other concerns — anonymous reporting available</p>
            </div>
            <ChevronRightIcon />
          </a>
        </div>
      </div>
    </>
  );
};

export default CampusSafety;
